import * as React from 'react';
import { AlertTriangle, ChevronRight, Info } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Callout,
  DetailEmpty,
  FilterChips,
  TriCheckbox,
  WorkspaceButton,
  WorkspaceSelect,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { useCoverageQuery, type AreaCoverage } from '@/lib/api/discovery';
import {
  confirmEvidence,
  declineEvidence,
  useConfirmPreviewQuery,
  useReviewQueueQuery,
  type ConfirmResult,
  type DeclineReason,
  type ReviewItem,
  type ReviewQueue as ReviewQueueData,
} from '@/lib/api/discovery-review';
import { useDiscoveryWrite } from '@/lib/api/discovery-sessions';
import { cn } from '@/lib/utils/cn';
import { plural } from '../discovery-logic';
import { errorText } from '../sessions/errors';
import { coverageEffect, coverageMoved } from '../sessions/sessions-logic';
import { DeclinedRow, ReviewItemRow, type Declined } from './review-item';
import {
  effectLine,
  groupByFirstArea,
  heldText,
  inScope,
  isConfident,
  numberRange,
  parseScope,
  queueSummary,
  scopeParam,
  selectionFor,
  skippedText,
  syncSelection,
  type ReviewScope,
} from './review-logic';

/**
 * One review queue across interviews and documents (the interview
 * simplification of 23 Oct 2026, design note section 3). Everything waiting
 * for review, grouped by each item's first area: confident items arrive
 * selected (Decision 39), anything is declined in place, an item's own
 * areas are edited on click, and the coverage effect of the selection is
 * said once in the sticky footer. Items held back (a speaker not matched or
 * only suggested by the AI, the interviewer, a same-person question) are
 * not listed: one line per interview says so, with the way to unblock it.
 *
 * The same component is the second step of one interview's page
 * (`embedded`, scoped to the interview). Every count comes from the API or
 * is a length of what it sent; confirm, preview, decline and retag use the
 * existing evidence routes (confirm in chunks of the API's 500).
 */

const CONFIRM_CHUNK = 500;
type Filter = 'all' | 'confident' | 'look';

/** One stable empty list while loading, so the selection effect runs once per change. */
const NO_ITEMS: ReviewItem[] = [];

export function ReviewQueue({
  engagementId,
  scope,
  embedded = false,
  onScope,
  onOpenInterview,
  interviewName,
  hideWho = false,
}: {
  engagementId: string;
  scope: ReviewScope;
  /** On one interviewee's page the name is the page's title: not repeated per item. */
  hideWho?: boolean;
  /** On one interview's page: no head or source picker, the footer inside the step. */
  embedded?: boolean;
  onScope?: (scope: ReviewScope) => void;
  /** Open an interview (the held-back line's "Who was in the room?"). */
  onOpenInterview?: (interviewId: string) => void;
  /** How an interview is named (its person first), from the interview list. */
  interviewName?: (interview: { id: string; code: string; title: string }) => string;
}) {
  const { toast } = useToast();
  const q = useReviewQueueQuery(engagementId);
  const coverage = useCoverageQuery(engagementId);
  const areas: ReadonlyArray<AreaCoverage> = coverage.data?.areas ?? [];
  const labelOf = (id: string) => areas.find((a) => a.area_id === id)?.label ?? '';
  const nameOf = (iv: { id: string; code: string; title: string }) =>
    interviewName?.(iv) ?? `${iv.title} (${iv.code})`;

  const all = q.data?.items ?? NO_ITEMS;
  // The scope by its address form, so a new object for the same scope changes nothing.
  const scopeKey = scopeParam(scope);
  const scoped = React.useMemo(
    () => all.filter((i) => inScope(i, parseScope(scopeKey))),
    [all, scopeKey],
  );
  const ready = React.useMemo(() => scoped.filter((i) => i.hold === null), [scoped]);
  // Said once per interview, for the items in scope (an area's or a document's too): the
  // speaker holds (unmatched, or only suggested) are one line.
  const held = mergeHeld(heldBackOf(scoped));

  const [filter, setFilter] = React.useState<Filter>('all');
  const [open, setOpen] = React.useState<Set<string>>(new Set());
  const [declined, setDeclined] = React.useState<Declined[]>([]);
  const opened = React.useRef(false);

  // Selection: the preselected items, following the queue as it changes.
  const [picked, setPicked] = React.useState<Set<string>>(() => selectionFor('high', ready));
  const before = React.useRef(new Map<string, boolean>());
  React.useEffect(() => {
    const seen = before.current;
    setPicked((cur) => syncSelection(cur, ready, seen));
    before.current = new Map(ready.map((p) => [p.id, false]));
  }, [ready]);

  const groups = groupByFirstArea(
    ready.filter((i) =>
      filter === 'confident' ? isConfident(i) : filter === 'look' ? !isConfident(i) : true,
    ),
    areas,
  );
  // The first group opens by itself, once the areas are known; the rest wait for a click.
  const firstKey = coverage.data && groups.length > 0 ? (groups[0]!.area_id ?? 'none') : null;
  React.useEffect(() => {
    if (opened.current || firstKey === null) return;
    opened.current = true;
    setOpen(new Set([firstKey]));
  }, [firstKey]);

  const ids = ready.filter((i) => picked.has(i.id)).map((i) => i.id);
  const preview = useConfirmPreviewQuery(engagementId, ids.slice(0, CONFIRM_CHUNK));
  const effect =
    ids.length > 0 && preview.data ? coverageEffect(preview.data.changes, labelOf) : [];
  const confirm = useDiscoveryWrite(engagementId, async (sel: string[]) => {
    const out: ConfirmResult = { confirmed: [], skipped: [], coverage_changes: [] };
    for (let i = 0; i < sel.length; i += CONFIRM_CHUNK) {
      const r = await confirmEvidence(engagementId, sel.slice(i, i + CONFIRM_CHUNK));
      out.confirmed.push(...r.confirmed);
      out.skipped.push(...r.skipped);
      out.coverage_changes.push(...r.coverage_changes);
    }
    return out;
  });
  const decline = useDiscoveryWrite(
    engagementId,
    async (v: { items: ReviewItem[]; code: DeclineReason; label: string; text?: string }) => {
      for (const it of v.items) {
        await declineEvidence(engagementId, it.id, {
          reason_code: v.code,
          ...(v.text ? { reason_text: v.text } : {}),
        });
      }
      return v;
    },
  );

  const onConfirm = () =>
    confirm.mutate(ids, {
      onSuccess: (r) => {
        const moved = coverageMoved(r.coverage_changes, labelOf);
        toast({
          message: `${plural(r.confirmed.length, 'evidence item')} confirmed${
            r.confirmed.length ? ` (${numberRange(r.confirmed.map((c) => c.number))})` : ''
          }. ${moved || 'Coverage unchanged: same people as before.'}${skippedText(r.skipped)}`,
        });
      },
      onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
    });
  const onDecline = (item: ReviewItem, code: DeclineReason, label: string, text?: string) =>
    decline.mutate(
      { items: [item], code, label, text },
      {
        onSuccess: () => {
          setDeclined((d) => [...d, { item, reason: label }]);
          setPicked((cur) => {
            const next = new Set(cur);
            next.delete(item.id);
            return next;
          });
        },
        onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
      },
    );
  const declineInterviewer = (interviewId: string) => {
    const items = scoped.filter((i) => i.interview?.id === interviewId && i.hold === 'interviewer');
    decline.mutate(
      { items, code: 'wrong_person', label: 'Wrong person' },
      {
        onSuccess: () =>
          toast({
            message: `Declined ${plural(items.length, 'item')} from the interviewer. They stay on record and never count.`,
          }),
        onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
      },
    );
  };

  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading what was said…
      </div>
    );
  }

  const nConfident = ready.filter(isConfident).length;
  const selectedAreas = new Set(
    ready.filter((i) => picked.has(i.id)).map((i) => i.area_ids[0] ?? 'none'),
  ).size;
  const shownIds = groups.flatMap((g) => g.items.map((i) => i.id));
  const allOpen = groups.length > 0 && groups.every((g) => open.has(g.area_id ?? 'none'));
  const selectIn = (mode: 'all' | 'high' | 'none') =>
    setPicked((cur) => {
      const next = new Set([...cur].filter((id) => !shownIds.includes(id)));
      for (const id of selectionFor(
        mode,
        ready.filter((i) => shownIds.includes(i.id)),
      ))
        next.add(id);
      return next;
    });

  const head = embedded ? null : (
    <div className="mb-3 flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="text-h2 font-semibold text-ink-900">Review what was said</h2>
        <div className="text-meta text-ink-500">
          {ready.length > 0
            ? `${queueSummary(ready)} Confident items (0.85 or more) arrive selected; nothing counts until you confirm.`
            : held.length > 0
              ? 'Nothing is ready yet.'
              : 'Nothing is waiting.'}
        </div>
      </div>
      <SourcePicker
        data={q.data}
        areas={areas}
        scope={scope}
        nameOf={nameOf}
        onScope={(s) => onScope?.(s)}
      />
    </div>
  );

  const heldLines = held.map((h) => (
    <Callout
      key={`${h.interview_id}:${h.reason}`}
      tone={h.reason === 'interviewer' ? 'note' : 'warn'}
      icon={AlertTriangle}
      className="mb-3"
      actions={
        h.reason === 'interviewer' ? (
          <WorkspaceButton
            disabled={decline.isPending}
            onClick={() => declineInterviewer(h.interview_id)}
          >
            Decline {h.count === 1 ? 'it' : `all ${h.count}`}
          </WorkspaceButton>
        ) : onOpenInterview && !embedded ? (
          <WorkspaceButton onClick={() => onOpenInterview(h.interview_id)}>
            Who was in the room?
          </WorkspaceButton>
        ) : undefined
      }
    >
      {embedded && h.reason === 'person_unresolved'
        ? `${plural(h.count, 'item waits', 'items wait')} until the same-person question above is answered.`
        : embedded && h.reason !== 'interviewer'
          ? `${plural(h.count, 'item waits', 'items wait')} until the speakers above are matched to people.`
          : heldText(h, nameOf({ id: h.interview_id, code: h.code, title: h.title }))}
    </Callout>
  ));

  if (ready.length === 0 && declined.length === 0) {
    return (
      <>
        {head}
        {heldLines}
        <DetailEmpty
          title={
            held.length > 0
              ? 'Nothing is ready to review yet'
              : embedded
                ? 'Nothing from this interview is waiting'
                : 'Nothing to review'
          }
          text={
            held.length > 0
              ? 'The items above wait on who was in the room.'
              : embedded
                ? 'Confirmed items are in Findings, by area.'
                : 'New items appear here as transcripts and documents are read.'
          }
        />
      </>
    );
  }

  return (
    <>
      {head}
      {heldLines}
      <div className="mb-1.5 mt-1 flex flex-wrap items-center gap-2">
        <FilterChips
          aria-label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All', count: ready.length },
            { value: 'confident', label: 'Confident', count: nConfident },
            { value: 'look', label: 'Needs a look', count: ready.length - nConfident },
          ]}
        />
        <span className="flex-1" />
        <span className="text-meta text-ink-500">Select</span>
        <button type="button" className={linkishClass()} onClick={() => selectIn('high')}>
          Confident
        </button>
        <button type="button" className={linkishClass()} onClick={() => selectIn('all')}>
          All
        </button>
        <button type="button" className={linkishClass()} onClick={() => selectIn('none')}>
          None
        </button>
        <button
          type="button"
          className={linkishClass()}
          onClick={() =>
            setOpen(allOpen ? new Set() : new Set(groups.map((g) => g.area_id ?? 'none')))
          }
        >
          {allOpen ? 'Close all' : 'Open all'}
        </button>
      </div>

      {groups.map((g) => {
        const key = g.area_id ?? 'none';
        const isOpen = open.has(key);
        const nSel = g.items.filter((i) => picked.has(i.id)).length;
        const box = nSel === 0 ? 'off' : nSel === g.items.length ? 'on' : 'mixed';
        const cov = g.area_id ? areas.find((a) => a.area_id === g.area_id) : undefined;
        const gone = declined.filter((d) => (d.item.area_ids[0] ?? 'none') === key);
        return (
          <section key={key} className="mb-2 rounded-card border border-ink-100 bg-paper-0">
            <div
              className={cn(
                'flex items-center gap-2.5 px-3 py-2.5',
                isOpen && 'border-b border-ink-100',
              )}
            >
              <button
                type="button"
                aria-expanded={isOpen}
                aria-label={`${isOpen ? 'Close' : 'Open'} ${g.label}`}
                className="flex rounded-chip text-ink-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                onClick={() =>
                  setOpen((cur) => {
                    const next = new Set(cur);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
                }
              >
                <ChevronRight
                  className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-90')}
                  aria-hidden="true"
                />
              </button>
              <TriCheckbox
                state={box}
                aria-label={`Select all in ${g.label}`}
                onChange={(on) =>
                  setPicked((cur) => {
                    const next = new Set(cur);
                    for (const i of g.items) {
                      if (on) next.add(i.id);
                      else next.delete(i.id);
                    }
                    return next;
                  })
                }
              />
              <button
                type="button"
                className="min-w-0 truncate text-left text-body-sm font-semibold text-ink-900"
                onClick={() =>
                  setOpen((cur) => {
                    const next = new Set(cur);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
                }
              >
                {g.label}
              </button>
              <span className="text-meta text-ink-500">
                {plural(g.items.length, 'item')} · {nSel} selected
              </span>
              <span className="flex-1" />
              {cov ? <CoverageNow area={cov} /> : null}
            </div>
            {isOpen ? (
              <div>
                {g.items.map((it) => (
                  <ReviewItemRow
                    key={it.id}
                    engagementId={engagementId}
                    item={it}
                    hideWho={hideWho}
                    picked={picked.has(it.id)}
                    onPick={(on) =>
                      setPicked((cur) => {
                        const next = new Set(cur);
                        if (on) next.add(it.id);
                        else next.delete(it.id);
                        return next;
                      })
                    }
                    areas={areas}
                    labelOf={labelOf}
                    declining={decline.isPending}
                    onDecline={(code, label, text) => onDecline(it, code, label, text)}
                  />
                ))}
                {gone.map((d) => (
                  <DeclinedRow key={d.item.id} d={d} />
                ))}
              </div>
            ) : null}
          </section>
        );
      })}

      <div
        className={cn(
          'sticky z-[1] mt-3.5 flex flex-col gap-2 border-t border-ink-100 bg-paper-0 py-3 shadow-[0_-6px_12px_rgba(24,46,88,0.06)]',
          embedded ? '-bottom-4 -mx-[18px] -mb-4 rounded-b-card px-[18px]' : '-mx-7 bottom-0 px-7',
        )}
      >
        {effect.length > 0 ? (
          <div
            className="flex gap-2 text-meta text-ink-700"
            role="status"
            aria-label="Coverage effect"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
            <span>{effectLine(effect)}</span>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 text-meta text-ink-500">
            {ids.length} of {ready.length} selected
            {ids.length > 0 ? ` in ${plural(selectedAreas, 'area')}` : ''}
          </span>
          <WorkspaceButton
            variant="primary"
            size="md"
            disabled={ids.length === 0 || confirm.isPending}
            onClick={onConfirm}
          >
            {confirm.isPending ? 'Confirming…' : `Confirm ${ids.length || ''}`.trim()}
          </WorkspaceButton>
        </div>
      </div>
    </>
  );
}

type HeldBack = ReviewQueueData['held_back'][number];

/** The held items in scope, per interview and reason (the server's `held_back`, for any scope). */
function heldBackOf(items: ReadonlyArray<ReviewItem>): HeldBack[] {
  const out = new Map<string, HeldBack>();
  for (const i of items) {
    if (i.hold === null || !i.interview) continue;
    const key = `${i.interview.id}:${i.hold}`;
    const row = out.get(key);
    if (row) row.count++;
    else
      out.set(key, {
        interview_id: i.interview.id,
        code: i.interview.code,
        title: i.interview.title,
        reason: i.hold,
        count: 1,
      });
  }
  return [...out.values()];
}

/** One line per interview and kind: `unmapped` and `speaker_unconfirmed` both wait on the room. */
function mergeHeld(rows: ReadonlyArray<HeldBack>): HeldBack[] {
  const out = new Map<string, HeldBack>();
  for (const h of rows) {
    const reason = h.reason === 'speaker_unconfirmed' ? 'unmapped' : h.reason;
    const key = `${h.interview_id}:${reason}`;
    const row = out.get(key);
    if (row) row.count += h.count;
    else out.set(key, { ...h, reason });
  }
  return [...out.values()];
}

/** The area's coverage now, from the coverage read: "Partly covered · 1 of 2 sources". */
function CoverageNow({ area }: { area: AreaCoverage }) {
  if (area.status === 'corroborated') {
    return (
      <StatusChip tone="healthy" size="sm">
        Covered · {plural(area.sources, 'source')}
      </StatusChip>
    );
  }
  if (area.status === 'partial') {
    return (
      <StatusChip tone="at-risk" size="sm">
        Partly covered · {area.sources} of {area.required} sources
      </StatusChip>
    );
  }
  return (
    <StatusChip tone="unassessed" size="sm">
      Not covered
    </StatusChip>
  );
}

/** All, one interview by name, one document, or one area. */
function SourcePicker({
  data,
  areas,
  scope,
  nameOf,
  onScope,
}: {
  data: ReviewQueueData;
  areas: ReadonlyArray<AreaCoverage>;
  scope: ReviewScope;
  nameOf: (iv: { id: string; code: string; title: string }) => string;
  onScope: (s: ReviewScope) => void;
}) {
  const interviews = new Map<string, { id: string; code: string; title: string }>();
  const documents = new Map<string, { id: string; title: string }>();
  for (const i of data.items) {
    if (i.hold !== null) continue;
    if (i.interview) interviews.set(i.interview.id, i.interview);
    if (i.document) documents.set(i.document.id, i.document);
  }
  const value = scopeParam(scope);
  return (
    <WorkspaceSelect
      aria-label="Show items from"
      className="h-[30px] w-auto max-w-[280px] py-0 text-meta"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        onScope(
          v === 'all'
            ? { kind: 'all' }
            : v.startsWith('doc:')
              ? { kind: 'document', id: v.slice(4) }
              : v.startsWith('area:')
                ? { kind: 'area', id: v.slice(5) }
                : { kind: 'interview', id: v },
        );
      }}
    >
      <option value="all">All interviews and documents</option>
      {interviews.size > 0 || scope.kind === 'interview' ? (
        <optgroup label="Interviews">
          {[...interviews.values()]
            .sort((a, b) => Number(a.code.slice(1)) - Number(b.code.slice(1)))
            .map((iv) => (
              <option key={iv.id} value={iv.id}>
                {nameOf(iv)}
              </option>
            ))}
          {scope.kind === 'interview' && !interviews.has(scope.id) ? (
            <option value={scope.id}>This interview</option>
          ) : null}
        </optgroup>
      ) : null}
      {documents.size > 0 || scope.kind === 'document' ? (
        <optgroup label="Documents">
          {[...documents.values()].map((d) => (
            <option key={d.id} value={`doc:${d.id}`}>
              {d.title}
            </option>
          ))}
          {scope.kind === 'document' && !documents.has(scope.id) ? (
            <option value={`doc:${scope.id}`}>This document</option>
          ) : null}
        </optgroup>
      ) : null}
      <optgroup label="Areas">
        {areas.map((a) => (
          <option key={a.area_id} value={`area:${a.area_id}`}>
            {a.label}
          </option>
        ))}
      </optgroup>
    </WorkspaceSelect>
  );
}
